import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Award, ClipboardCheck, GraduationCap } from 'lucide-react';

import { getMyTraining, type MyEnrollment } from '@/api/training.api';
import { formatSopIdentifier } from '@/api/sops.api';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

/**
 * My Training — the trainee's home (Stream C, ADR-025). One composite
 * read (`/api/training/me`) feeds three sections: assigned courses with
 * progress + due dates, pending SOP acknowledgments, and earned
 * certificates.
 */

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function DueBadge({ enrollment }: { enrollment: MyEnrollment }) {
  if (enrollment.completedAt) {
    return <Badge variant="success">Completed {shortDate(enrollment.completedAt)}</Badge>;
  }
  if (!enrollment.dueAt) return null;
  if (enrollment.isOverdue) {
    return <Badge variant="danger">Overdue — was due {shortDate(enrollment.dueAt)}</Badge>;
  }
  return <Badge variant="neutral">Due {shortDate(enrollment.dueAt)}</Badge>;
}

export function MyTrainingPage() {
  const navigate = useNavigate();
  const trainingQuery = useQuery({ queryKey: ['training', 'me'], queryFn: getMyTraining });

  if (trainingQuery.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
      </div>
    );
  }

  if (trainingQuery.error || !trainingQuery.data) {
    return (
      <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
        <div>
          <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load your training</p>
          <p className="mt-1 text-[13px] text-kw-danger-text/80">
            {trainingQuery.error instanceof Error ? trainingQuery.error.message : 'Unknown error'}
          </p>
        </div>
      </Card>
    );
  }

  const { enrollments, pendingSopAcks, certificates } = trainingQuery.data;
  const active = enrollments.filter((e) => e.completedAt === null);
  const completed = enrollments.filter((e) => e.completedAt !== null);

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="typo-h1">My Training</h1>
        <p className="mt-1 text-[14px] text-kw-muted">
          Assigned courses, SOP acknowledgments, and your certificates.
        </p>
      </div>

      {/* Assigned courses */}
      <section className="space-y-2">
        <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">Assigned courses</h2>
        <Card className="p-0">
          {active.length === 0 && completed.length === 0 ? (
            <div className="flex flex-col items-center gap-3 p-12 text-center">
              <GraduationCap className="size-10 text-kw-ghost" />
              <div>
                <p className="text-[15px] font-medium text-kw-shark">No training assigned</p>
                <p className="mt-1 text-[13px] text-kw-muted">Courses assigned to you will appear here.</p>
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-kw-border">
              {[...active, ...completed].map((e) => {
                const { completedLessons, totalLessons } = e.progress;
                const pct = totalLessons === 0 ? 0 : Math.round((completedLessons / totalLessons) * 100);
                return (
                  <li key={e.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] font-medium text-kw-shark">{e.course.title}</p>
                      <div className="mt-1.5 flex items-center gap-2">
                        <div
                          className="h-1.5 w-32 overflow-hidden rounded-kw-full bg-kw-bg"
                          role="progressbar"
                          aria-label={`Progress for ${e.course.title}`}
                          aria-valuenow={completedLessons}
                          aria-valuemin={0}
                          aria-valuemax={totalLessons}
                        >
                          <div
                            className={e.completedAt ? 'h-full bg-kw-tag-text' : 'h-full bg-kw-cerulean'}
                            style={{ width: `${String(pct)}%` }}
                          />
                        </div>
                        <span className="text-[11px] text-kw-muted">
                          {completedLessons}/{totalLessons} lessons
                          {e.completedAt ? ' · quiz passed' : ''}
                        </span>
                      </div>
                    </div>
                    <DueBadge enrollment={e} />
                    {e.completedAt ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          void navigate(`/training/courses/${e.courseId}`);
                        }}
                      >
                        Review
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => {
                          void navigate(`/training/courses/${e.courseId}`);
                        }}
                      >
                        {e.startedAt ? 'Continue' : 'Start'}
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </section>

      {/* Pending SOP acknowledgments */}
      <section className="space-y-2">
        <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">
          Pending SOP acknowledgments
        </h2>
        <Card className="p-0">
          {pendingSopAcks.length === 0 ? (
            <div className="flex items-center gap-3 px-4 py-6">
              <ClipboardCheck className="size-5 text-kw-tag-text" />
              <p className="text-[13px] text-kw-muted">
                All caught up — no SOPs waiting for your acknowledgment.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-kw-border">
              {pendingSopAcks.map((ack) => (
                <li key={ack.sopVersionId} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] text-kw-shark">
                      <span className="font-mono text-[12px] text-kw-muted">{formatSopIdentifier(ack)}</span>{' '}
                      {ack.title}
                    </p>
                    <p className="mt-0.5 text-[11px] text-kw-muted">Revision {ack.revision}</p>
                  </div>
                  <Link
                    to={`/compliance/sops/${ack.slug}`}
                    className="text-[13px] font-medium text-kw-cerulean hover:underline"
                  >
                    Read &amp; acknowledge
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>

      {/* Certificates */}
      <section className="space-y-2">
        <h2 className="text-[13px] font-medium uppercase tracking-wide text-kw-muted">My certificates</h2>
        <Card className="p-0">
          {certificates.length === 0 ? (
            <div className="flex items-center gap-3 px-4 py-6">
              <Award className="size-5 text-kw-ghost" />
              <p className="text-[13px] text-kw-muted">
                Certificates appear here when you complete a course.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-kw-border">
              {certificates.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <Award className="size-4 shrink-0 text-kw-cerulean" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-medium text-kw-shark">{c.courseTitleSnapshot}</p>
                    <p className="mt-0.5 text-[11px] text-kw-muted">
                      Completed {shortDate(c.completedAt)}
                      {c.quizScorePercent !== null ? ` · score ${String(c.quizScorePercent)}%` : ''}
                      {c.expiresAt ? ` · expires ${shortDate(c.expiresAt)}` : ''}
                    </p>
                  </div>
                  <Link
                    to={`/training/certificates/${c.certificateCode}`}
                    className="text-[13px] font-medium text-kw-cerulean hover:underline"
                  >
                    View certificate
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>
    </div>
  );
}
